/**
 * AttachmentList — rows for request attachments.
 *
 * Each row: type icon, filename (truncated + tooltip), size, progress bar
 * while uploading, error text, and Retry / Remove actions. A server-stored
 * file's name is a button that downloads it with auth. With `readOnly` the
 * Remove/Retry actions are hidden (confirmed requests, requirement detail).
 */
import {
  Close as CloseIcon,
  DescriptionOutlined as DocIcon,
  ImageOutlined as ImageIcon,
  PictureAsPdfOutlined as PdfIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import { Alert, Box, ButtonBase, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState, type ReactElement } from 'react';

import type { AttachmentItem } from '@/hooks/useAttachmentUploads';
import { ApiError } from '@/helpers/ApiHelper';
import {
  IMAGE_ATTACHMENT_EXTENSIONS,
  downloadAttachment,
  extensionOf,
  formatBytes,
} from '@/lib/attachments';

function FileTypeIcon({ filename }: { filename: string }): ReactElement {
  const ext = extensionOf(filename);
  if (ext === '.pdf') return <PdfIcon fontSize="small" aria-hidden />;
  if ((IMAGE_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return <ImageIcon fontSize="small" aria-hidden />;
  }
  return <DocIcon fontSize="small" aria-hidden />;
}

export interface AttachmentListProps {
  items: AttachmentItem[];
  readOnly?: boolean;
  onRemove?: (key: string) => void;
  onRetry?: (key: string) => void;
  /** Disable Remove/Retry (e.g. while the wizard is busy). */
  disabled?: boolean;
}

export function AttachmentList({
  items,
  readOnly = false,
  onRemove,
  onRetry,
  disabled = false,
}: AttachmentListProps): ReactElement | null {
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async (item: AttachmentItem) => {
    if (!item.attachment) return;
    setDownloadError(null);
    try {
      await downloadAttachment(item.attachment.id, item.filename);
    } catch (e) {
      setDownloadError(
        `Could not download ${item.filename}${e instanceof ApiError && e.detail ? `: ${e.detail}` : ''}`,
      );
    }
  }, []);

  if (items.length === 0) return null;

  return (
    <Box>
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Attached files">
        {items.map((item) => {
          const canDownload = item.status === 'done' && item.attachment !== null;
          const failed = item.status === 'error';
          return (
            <Box
              component="li"
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                minHeight: 52,
                border: 1,
                borderColor: failed ? 'error.main' : 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ color: failed ? 'error.main' : 'text.secondary', display: 'flex' }}>
                <FileTypeIcon filename={item.filename} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, py: 0.75 }}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Tooltip title={item.filename} enterDelay={400}>
                    {canDownload ? (
                      <ButtonBase
                        onClick={() => void handleDownload(item)}
                        aria-label={`Download ${item.filename}`}
                        sx={{
                          minHeight: 44,
                          minWidth: 0,
                          maxWidth: '100%',
                          justifyContent: 'flex-start',
                          '&:focus-visible': {
                            outline: (t) => `2px solid ${t.palette.primary.main}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ color: 'primary.main', textDecoration: 'underline' }}
                        >
                          {item.filename}
                        </Typography>
                      </ButtonBase>
                    ) : (
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {item.filename}
                      </Typography>
                    )}
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatBytes(item.size_bytes)}
                  </Typography>
                </Stack>
                {item.status === 'uploading' && (
                  <LinearProgress
                    variant="determinate"
                    value={item.progress}
                    aria-label={`Uploading ${item.filename}`}
                    sx={{ mt: 0.5 }}
                  />
                )}
                {item.status === 'queued' && (
                  <Typography variant="body2" color="text.secondary">
                    Will upload when you start the request
                  </Typography>
                )}
                {item.error && (
                  <Typography variant="body2" color="error" role="alert" sx={{ display: 'block' }}>
                    {item.error}
                  </Typography>
                )}
              </Box>
              {!readOnly && failed && item.retryable && onRetry && (
                <Tooltip title="Retry upload">
                  <span>
                    <IconButton
                      size="large"
                      aria-label={`Retry upload of ${item.filename}`}
                      onClick={() => onRetry(item.key)}
                      disabled={disabled}
                    >
                      <RetryIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!readOnly && onRemove && item.status !== 'uploading' && (
                <Tooltip title="Remove">
                  <span>
                    <IconButton
                      size="large"
                      aria-label={`Remove ${item.filename}`}
                      onClick={() => onRemove(item.key)}
                      disabled={disabled}
                    >
                      <CloseIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Stack>
      {downloadError && (
        <Alert severity="error" role="alert" sx={{ mt: 1 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}
    </Box>
  );
}
